import * as Sentry from '@sentry/browser';
import { localize } from 'i18n/localize';
import { rejoinMessengerRoom } from 'service/offer/resource';
import { toastMessages } from 'components/toastMessage/signals';
import { tld } from 'utils/env';

const $t = localize();

type SznMessenger = Element & {
	ready: Promise<void>;
	showGroup(labelId: string): void;
	showList(): void;
	showRoom(roomId: string): Element | undefined;
};

type LoginWidget = Element & {
	loadMess(): Promise<SznMessenger>;
};

export type UserUnreadChangeEvent = CustomEvent<{
	id: string;
	type: 'user-unread-change';
	unread: { count: number; room: { advertId: string; count: number; id: string; lastReadAt: number } };
}>;

const DEFAULT_MESSENGER_LOAD_TIMEOUT = 10 * 1000;
const MESSENGER_URL_PARAM = 'messenger';
const MESSENGER_ELEMENT_NAME = 'm-app';

export const MESSENGER_EVENTS = {
	USER_UNREAD_CHANGE: 'user-unread-change',
	ROOM_SHOW: 'room-show',
} as const;

export class Messenger {
	static async tryOpenOnPageLoad({
		offerId,
		openGroup,
		options = { timeout: DEFAULT_MESSENGER_LOAD_TIMEOUT },
		roomId,
	}: {
		offerId?: string;
		openGroup?: boolean;
		options?: { timeout?: number };
		roomId?: string;
	}) {
		if (this.hasUrlMessengerParam(window.location.href)) {
			if (offerId && openGroup) {
				try {
					await this.openGroup(offerId, options);
				} catch {
					await this.openMessenger(options).catch(Messenger.showErrorToast);
				}
			} else if (offerId && !openGroup) {
				try {
					const messengerRoomId = roomId ?? (await rejoinMessengerRoom(offerId)).messengerRoomId;
					await this.openRoom(messengerRoomId, options).catch(Messenger.showErrorToast);
				} catch {
					await this.openMessenger(options).catch(Messenger.showErrorToast);
				}
			} else {
				await this.openMessenger(options).catch(Messenger.showErrorToast);
			}

			removeMessengerParamFromUrl();
		}
	}

	static async openGroup(labelId: string, options: { timeout?: number } = {}) {
		try {
			const sznMessenger = await this.openMessenger(options);
			sznMessenger.showGroup(labelId);
		} catch (error) {
			Sentry.captureException(error);
			throw error;
		}
	}

	static async openMessenger(options: { timeout?: number } = {}) {
		try {
			const sznMessenger = await getSznMessenger(options);
			sznMessenger.showList();
			return sznMessenger;
		} catch (error) {
			Sentry.captureException(error);
			throw error;
		}
	}

	static openRoom(roomId: string, options: { timeout?: number } = {}) {
		return new Promise<void>((resolve, reject) => {
			getSznMessenger(options)
				.then((sznMessenger) => {
					const step = 100;
					let timeout = 1000;
					const interval = setInterval(() => {
						const roomElm = sznMessenger.showRoom(roomId);

						if (roomElm) {
							clearInterval(interval);
							resolve();
							return;
						}

						if (timeout <= 0) {
							clearInterval(interval);
							reject(new Error('Opening messenger room timed out'));
							return;
						}

						timeout -= step;
					}, step);
				})
				.catch((error) => {
					Sentry.captureException(error);
					reject(new Error('Failed to open room.'));
				});
		});
	}

	static showErrorToast = () => {
		toastMessages.error($t.messenger.openError);
	};

	static showErrorUserBlocked = () => {
		toastMessages.error($t.messenger.userBlockedError);
	};

	static hasUrlMessengerParam(url: string) {
		return new URL(url).hash.includes(MESSENGER_URL_PARAM);
	}

	/**
	 * MESSENGER_BETA
	 * Check if the messenger is enabled for the user (Seznam.cz employee) or environment (dev).
	 * It could be deleted after enabling messenger for all users.
	 */
	static isEnabled(userLogin: string | undefined) {
		if (tld() === 'dev.dszn.cz') return true;
		if (!userLogin) return false;
		return userLogin.endsWith('@firma.seznam.cz');
	}
}

async function getSznMessenger({ timeout }: { timeout?: number } = {}) {
	const loginWidget = await getLoginWidget(timeout);
	if (loginWidget === null) throw new Error('Login widget not found');

	const sznMessengerPromise = loginWidget.loadMess();
	const timeoutPromise = new Promise<never>((_, reject) =>
		setTimeout(() => reject(new Error('loadMess timed out')), 5000),
	);
	const sznMessenger = await Promise.race([sznMessengerPromise, timeoutPromise]);
	await sznMessenger.ready;
	return sznMessenger;
}

async function getLoginWidget(timeout: number = 0, step: number = 100): Promise<LoginWidget | null> {
	const widget = document.querySelector<LoginWidget>('szn-login-widget');
	if (widget) {
		return widget;
	}

	return new Promise((resolve, _) => {
		let counter = 0;

		const interval = setInterval(() => {
			const widget = document.querySelector<LoginWidget>('szn-login-widget');
			if (widget !== null) {
				clearInterval(interval);
				resolve(widget);
			}

			if (counter >= timeout) {
				Sentry.captureException(new Error('Get login widget timeout'));
				clearInterval(interval);
				resolve(null);
			}

			counter += step;
		}, step);
	});
}

function removeMessengerParamFromUrl() {
	const urlWithoutHash = window.location.href.split('#')[0];
	history.replaceState(history.state, '', urlWithoutHash);
}

export function observeMessengerLoaded(callback: (mess: SznMessenger) => void) {
	const observer = new MutationObserver((mutationList, observer) => {
		for (const mutation of mutationList) {
			if (mutation.type !== 'childList' || !mutation.addedNodes.length) {
				continue;
			}

			const messApp = mutation.addedNodes
				.values()
				.find((addedNode) => addedNode.nodeName.toLocaleLowerCase() === MESSENGER_ELEMENT_NAME);
			if (messApp) {
				callback(messApp as SznMessenger);
				observer.disconnect();
				return;
			}
		}
	});

	// m-app is defined on hover or click opener icon or on messenger load via JavaScript API
	customElements.whenDefined(MESSENGER_ELEMENT_NAME).then(async () => {
		const loginWidget = await getLoginWidget();
		if (loginWidget && loginWidget.shadowRoot) {
			observer.observe(loginWidget.shadowRoot, {
				childList: true,
			});
		}
	});
}
